import type { CompanyBrief, IndustryBrief, ResearchSource, ResearchTone } from "../../shared/types";

export type { ResearchTone } from "../../shared/types";

export const companyBrief: CompanyBrief = {
  identity: {
    name: "京东方A",
    code: "000725.SZ",
    industry: "半导体显示 · 面板制造",
    position: "全球大尺寸显示出货龙头",
    updatedAt: "2026-07-18 11:20",
    coverage: "28 条事实 · 7 个数据源",
    tags: ["LCD 现金牛", "OLED 放量", "玻璃基板 0→1"],
  },
  view: {
    label: "积极跟踪",
    score: 78,
    confidence: "中高",
    thesis: "LCD 盈利稳定化提供现金流底盘，8.6 代 AMOLED 打开 IT 中尺寸增量，玻璃基板决定估值弹性。",
    counter: "OLED 折旧爬坡、下游需求波动和新业务兑现节奏，仍可能压低利润质量与估值中枢。",
  },
  snapshot: [
    { label: "2025 营业收入", value: "2,045.9", unit: "亿元", change: "+3.13%", tone: "up" as ResearchTone, note: "重回 2,000 亿规模", series: [72, 76, 88, 91, 95] },
    { label: "2025 归母净利润", value: "58.57", unit: "亿元", change: "+10.03%", tone: "up" as ResearchTone, note: "连续两年修复", series: [36, 45, 66, 74, 82] },
    { label: "2025 经营现金流", value: "488.25", unit: "亿元", change: "+2.28%", tone: "up" as ResearchTone, note: "净利润的 8.3 倍", series: [62, 74, 83, 86, 88] },
    { label: "2025 研发投入", value: "139.83", unit: "亿元", change: "+5.89%", tone: "up" as ResearchTone, note: "占营收 6.83%", series: [55, 61, 68, 77, 84] },
    { label: "2026Q1 归母净利", value: "17.07", unit: "亿元", change: "+5.78%", tone: "up" as ResearchTone, note: "净利率 3.35%", series: [58, 63, 70, 73, 79] },
    { label: "2026H1 净利预告", value: "50–55", unit: "亿元", change: "+54%–69%", tone: "up" as ResearchTone, note: "Q2 环比显著加速", series: [42, 48, 57, 73, 94] },
  ],
  logic: [
    { horizon: "NOW", title: "业绩拐点被财务数据确认", detail: "2026H1 归母净利润预计 50–55 亿元；扣非净利润预计同比增长 35%–45%。", evidence: "业绩预告 · 2026-07-09", tone: "up" as ResearchTone },
    { horizon: "1–2Y", title: "8.6 代 AMOLED 切入 IT 中尺寸", detail: "成都产线已量产并交付，覆盖柔性 OLED 与 Hybrid OLED，产品指向笔电、平板与车载。", evidence: "公司新闻 · 2026-06-17", tone: "up" as ResearchTone },
    { horizon: "3Y+", title: "玻璃基板构成估值期权", detail: "试验线、客户送样和量产决策是关键里程碑；当前仍应按高不确定性新业务定价。", evidence: "投资者交流与样稿信息", tone: "neutral" as ResearchTone },
  ],
  financialTrend: [
    { period: "2023A", revenue: 1745.43, profit: 25.47, roe: 1.89 },
    { period: "2024A", revenue: 1983.81, profit: 53.23, roe: 4.05 },
    { period: "2025A", revenue: 2045.90, profit: 58.57, roe: 4.39 },
  ],
  financialHighlights: [
    { label: "ROE", value: "1.89% → 4.39%", tone: "up" },
    { label: "经营现金流 / 净利润", value: "8.3×", tone: "neutral" },
    { label: "2026H1E 净利", value: "+54%–69%", tone: "up" },
  ],
  businessMix: [
    { name: "显示器件", share: 81.34, growth: 0.86, color: "#a63d35" },
    { name: "物联网创新", share: 19.04, growth: 15.14, color: "#2f6650" },
    { name: "MLED", share: 4.55, growth: 9.81, color: "#b8894f" },
    { name: "传感", share: 0.29, growth: 52.00, color: "#4f6f93" },
  ],
  businessMixTotal: "2,045.9 亿元营收",
  businessMixNote: "分业务收入存在内部抵销，图中占比按公司披露口径展示；传感业务体量小但增速与毛利率领先。",
  marketPosition: [
    { metric: "大尺寸显示面板出货份额", value: 35, rank: "全球 #1", source: "Omdia · 2025A" },
    { metric: "柔性 OLED 产能份额", value: 25, rank: "全球 #2", source: "Counterpoint · 2025A" },
    { metric: "车载 OLED 出货份额", value: 12, display: "低双位数", rank: "全球 #2", source: "Omdia · 2025E" },
  ],
  positionConclusion: "京东方的护城河首先是规模和客户，其次才是单点技术。LCD 的份额优势已经建立；OLED 的关键是把产能份额转化为稳定利润。",
  timeline: [
    { date: "2026.06", title: "8.6 代 AMOLED 量产交付", status: "已发生", tone: "up" as ResearchTone },
    { date: "2026H2", title: "中尺寸 OLED 客户验证与产能爬坡", status: "跟踪中", tone: "neutral" as ResearchTone },
    { date: "2026H2", title: "钙钛矿户外实证测试", status: "待验证", tone: "neutral" as ResearchTone },
    { date: "2027H1", title: "玻璃基板量产投资决策", status: "关键节点", tone: "down" as ResearchTone },
    { date: "2028E", title: "玻璃基板 / 光互连商业化", status: "远期假设", tone: "neutral" as ResearchTone },
  ],
  peers: [
    { name: "京东方A", code: "000725.SZ", role: "综合显示龙头", focus: "LCD / OLED / MLED", edge: "规模、客户、产线代际", signal: "基准" },
    { name: "TCL科技", code: "000100.SZ", role: "大尺寸龙头", focus: "LCD / 印刷 OLED", edge: "高世代线与效率", signal: "强对手" },
    { name: "三星电子", code: "005930.KS", role: "OLED 全球龙头", focus: "柔性 OLED / QD-OLED", edge: "技术与高端客户", signal: "技术标杆" },
    { name: "LG Display", code: "LPL.US", role: "大尺寸 OLED 龙头", focus: "WOLED / P-OLED", edge: "高端电视与车载", signal: "细分标杆" },
    { name: "深天马A", code: "000050.SZ", role: "中小尺寸追赶者", focus: "车载 / LTPS / OLED", edge: "车载显示", signal: "细分对手" },
  ],
  earningsForecast: [
    { period: "2025A", profit: 58.57, growth: 10.03 },
    { period: "2026E", profit: 87.72, growth: 49.77 },
    { period: "2027E", profit: 112.75, growth: 28.53 },
    { period: "2028E", profit: 142.51, growth: 26.39 },
  ],
  forecastLabel: "样稿 / Wind 演示口径 · 需终端校验",
  valuation: [
    { label: "PB", value: "1.74×", note: "历史中枢附近" },
    { label: "2026E PE", value: "26.9×", note: "Wind 一致预期口径" },
    { label: "盈利 CAGR", value: "34.5%", note: "2025A–2028E" },
  ],
  valuationNote: "主业仍适合 PB 与现金流框架；若 OLED 盈利和玻璃基板商业化兑现，才具备向 PE / DCF 切换的条件。",
  chain: {
    customers: ["Apple", "华为", "荣耀", "OPPO", "vivo", "小米", "联想", "车载 Tier 1"],
    suppliers: ["康宁 · 玻璃基材", "莱特光电 · OLED 材料", "联得装备 · 贴合设备", "帝尔激光 · TGV 设备"],
  },
  questions: [
    "8.6 代 AMOLED 的客户导入、良率和月度稼动率爬坡节奏？",
    "LCD 高世代线折旧下降将在 2026–2028 年释放多少利润？",
    "玻璃基板送样客户数量、验证阶段和量产决策门槛？",
    "显示主业与创新业务之间的资本开支如何分配？",
  ],
  risks: [
    { title: "终端需求", detail: "手机、PC、TV 需求低于预期将压制出货与价格。", level: "高" },
    { title: "OLED 盈利", detail: "折旧、良率和价格竞争可能延迟盈利拐点。", level: "高" },
    { title: "新业务兑现", detail: "玻璃基板仍处于客户验证和产业化早期。", level: "中高" },
    { title: "贸易与专利", detail: "国际贸易政策及知识产权争议可能影响海外业务。", level: "中" },
  ],
  outlook: {
    shortTerm: { title: "短期：LCD 稳价与 OLED 减亏决定盈利弹性", thesis: "未来两个报告期的核心不是收入规模，而是面板价格、稼动率与 OLED 良率共同驱动的利润兑现。", evidence: ["LCD 供给格局较上一轮周期更集中", "OLED 产线进入客户导入与良率爬坡阶段"], watch: ["月度面板价格与稼动率", "OLED 单季亏损收窄幅度"] },
    longTerm: { title: "长期：显示龙头向高端显示与先进封装延伸", thesis: "长期估值重心取决于高端 OLED 占比、稳定自由现金流以及玻璃基板能否从技术验证进入规模收入。", evidence: ["大尺寸 LCD 具备规模和客户基础", "8.6 代 OLED 与玻璃基板提供第二曲线"], watch: ["高端产品收入和毛利占比", "新业务客户认证与资本回报"] },
    catalysts: [
      { date: "2026H2", event: "OLED 客户导入提速", impact: "改善产品结构与亏损预期", verification: "定期报告 / 业绩说明会" },
      { date: "2026H2", event: "LCD 旺季价格表现", impact: "决定主业盈利上修空间", verification: "行业报价 / 公司公告" },
      { date: "2027", event: "8.6 代线爬坡", impact: "打开 IT OLED 增长空间", verification: "产线进度 / 客户认证" },
      { date: "2027+", event: "玻璃基板商业化", impact: "可能形成新的估值叙事", verification: "量产订单 / 收入披露" },
    ],
    plans: [
      { area: "高端显示", detail: "推进 8.6 代 AMOLED 产线建设与客户导入。", verification: "项目公告 / 年报" },
      { area: "经营质量", detail: "提升 OLED 良率和稼动率，优化成熟 LCD 资产回报。", verification: "分部盈利 / 业绩说明会" },
      { area: "创新业务", detail: "推进玻璃基板与传感等业务的客户验证和产业化。", verification: "订单 / 收入与资本开支" },
    ],
    conclusion: "短期关注主业盈利兑现，长期关注 OLED 和玻璃基板能否把规模优势转化为更高、更稳定的资本回报。当前更适合以可验证里程碑逐级上修信心，而不是一次性计入全部远期空间。",
  },
};

export const industryBrief: IndustryBrief = {
  identity: {
    name: "半导体显示面板",
    code: "LCD · OLED · MLED · 先进封装玻璃基板",
    stage: "LCD 成熟收获 / OLED 成长扩张",
    updatedAt: "2026-07-18 11:20",
    coverage: "16 家公司 · 4 个细分赛道",
    tags: ["供给格局优化", "IT OLED 渗透", "国产龙头集中"],
  },
  view: {
    label: "中性偏多",
    score: 74,
    thesis: "LCD 的价值来自供给纪律和折旧下降，OLED 的价值来自 IT 与车载渗透；行业收益将更多向具备规模、客户与技术迭代能力的龙头集中。",
  },
  metrics: [
    { label: "2025 大尺寸面板出货", value: "910.5", unit: "百万片", change: "+2.9% YoY", tone: "up" as ResearchTone },
    { label: "2026 OLED 平板出货", value: "15", unit: "百万片 E", change: "+39% YoY", tone: "up" as ResearchTone },
    { label: "2025 车载 OLED 出货", value: "3.05", unit: "百万片 E", change: "+11.8% YoY", tone: "up" as ResearchTone },
    { label: "BOE 柔性 OLED 产能", value: "25%", unit: "份额", change: "全球 #2", tone: "neutral" as ResearchTone },
  ],
  segments: [
    { name: "LCD", stage: "成熟 / 弱周期", score: 68, demand: "TV 大尺寸化、IT 换机", supply: "大陆双寡头、按需生产", margin: "折旧下降改善", tone: "neutral" as ResearchTone },
    { name: "柔性 OLED", stage: "成长 / 仍过剩", score: 76, demand: "高端手机、折叠屏", supply: "三星领先、BOE 追赶", margin: "结构升级优先", tone: "up" as ResearchTone },
    { name: "IT OLED", stage: "0→1 放量", score: 84, demand: "平板、笔电、显示器", supply: "8.6 代线进入爬坡", margin: "高价值但高折旧", tone: "up" as ResearchTone },
    { name: "玻璃基板", stage: "产业化前夜", score: 61, demand: "AI 先进封装", supply: "路线与客户验证中", margin: "期权价值", tone: "down" as ResearchTone },
  ],
  demandTrend: [
    { label: "大尺寸显示", base: 100, next: 103, note: "2025A +2.9%" },
    { label: "车载 OLED", base: 100, next: 112, note: "2025E +11.8%" },
    { label: "平板 OLED", base: 100, next: 139, note: "2026E +39%" },
    { label: "OLED 显示器", base: 100, next: 192, note: "2025A +92%" },
  ],
  demandNote: "不同数据对应不同年度与预测口径，只用于比较增长弹性，不代表绝对市场规模。",
  valueChain: [
    { stage: "上游材料 / 设备", companies: ["康宁", "莱特光电", "奥来德", "联得装备", "帝尔激光"], signal: "材料国产化 + TGV 设备验证" },
    { stage: "面板制造", companies: ["京东方", "TCL 华星", "三星显示", "LGD", "深天马", "维信诺"], signal: "规模、良率、客户决定份额" },
    { stage: "终端品牌", companies: ["Apple", "Samsung", "华为", "联想", "车企"], signal: "IT OLED 与车载渗透率" },
  ],
  leaders: [
    { company: "京东方A", code: "000725.SZ", exposure: "LCD / OLED / MLED", position: "大尺寸 #1；柔性 OLED #2", watch: "8.6 代线爬坡" },
    { company: "TCL科技", code: "000100.SZ", exposure: "LCD / 印刷 OLED", position: "大尺寸全球前二", watch: "供给纪律与 IT 布局" },
    { company: "三星电子", code: "005930.KS", exposure: "柔性 OLED / QD-OLED", position: "高端 OLED 龙头", watch: "IT OLED 量产" },
    { company: "LG Display", code: "LPL.US", exposure: "WOLED / 车载 OLED", position: "大尺寸 OLED 龙头", watch: "盈利修复" },
    { company: "深天马A", code: "000050.SZ", exposure: "车载 / 中小尺寸", position: "车载显示核心玩家", watch: "OLED 产能利用率" },
    { company: "维信诺", code: "002387.SZ", exposure: "柔性 AMOLED", position: "国内 OLED 追赶者", watch: "新产线与客户" },
  ],
  catalysts: [
    "8.6 代 IT OLED 产线集中进入客户导入和爬坡阶段",
    "AI PC、平板和高刷新显示器推动 OLED 渗透",
    "LCD 供给纪律延续，龙头高世代线折旧下降",
    "玻璃基板完成客户验证并出现明确量产投资",
  ],
  risks: [
    "终端需求恢复弱于产能扩张，OLED 供需继续宽松",
    "价格竞争抵消产品结构升级与成本下降",
    "新产线良率和客户导入慢于预期",
    "贸易、专利与地缘政治扰动全球客户结构",
  ],
};

export const researchSources: ResearchSource[] = [
  { label: "京东方 2025 年年度报告", type: "公司公告", date: "2026-04-01", url: "https://static.cninfo.com.cn/finalpage/2026-04-01/1225068510.PDF" },
  { label: "京东方 2026 年半年度业绩预告", type: "公司公告", date: "2026-07-09", url: "https://static.cninfo.com.cn/finalpage/2026-07-09/1225415802.PDF" },
  { label: "第 8.6 代 AMOLED 量产暨客户交付", type: "公司新闻", date: "2026-06-17", url: "https://www.boe.com/company/dynamic-ccd27064a69749e38bf083a2523a9df8" },
  { label: "Large-area display shipments 2025", type: "Omdia", date: "2026-01-13", url: "https://omdia.tech.informa.com/pr/2026/jan/large-area-display-shipments-grew-2point9percent-year-over-year-in-2025" },
  { label: "OLED supply / demand update", type: "Counterpoint", date: "2026-05", url: "https://counterpointresearch.com/en/reports/oled-supply-demand-update-demand-slowdown-exacerbates-oversupply" },
  { label: "Tablet OLED shipments outlook", type: "Omdia", date: "2025-11-17", url: "https://omdia.tech.informa.com/pr/2025/nov/tablet-oled-display-shipments-to-grow-39percent-yoy-in-2026-while-lcd-remains-stable" },
];
